'use client';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS, Node } from '@contentful/rich-text-types';
import { RichTextHeadlineProps } from './types';
import { Document } from '@contentful/rich-text-types';
import { getCleanDocument } from 'utils/formatters';
import { Node as ContentfulNode } from '@contentful/rich-text-types';
import Link from 'components/shared/Link/Link';
import renderEmbeddedEntry from 'components/nodes/EmbeddedEntry';
import { rewriteEnterpriseSlug } from 'utils/enterprise';
import { cn } from 'utils/styles';
import { renderSuperscript } from 'utils/superscriptUtils';
import { useDisclosureNumberingMap } from 'contexts/DisclosureProvider';

const RichTextHeadline = ({ headline, level, additionalOptions, className }: RichTextHeadlineProps) => {
  // Use the disclosure numbering map from context
  const disclosureNumberingMap = useDisclosureNumberingMap();

  if (!headline) {
    return null;
  }

  // Keep original headline for links logic
  const originalHeadline = headline;

  // Clean document for rendering
  const cleanHeadline = getCleanDocument(headline) as Document;

  const renderEntryLink = (node: ContentfulNode, children: React.ReactNode) => {
    const entryLink =
      'links' in originalHeadline &&
      originalHeadline.links?.entries?.hyperlink?.find(entry => entry.sys.id === node.data.target.sys.id);

    if (entryLink) {
      if (entryLink.__typename === 'Page' && entryLink.slug) {
        const slug = rewriteEnterpriseSlug(entryLink.slug);

        return (
          <Link href={`/${slug}`} className="inline underline" data-testid="inline-hyperlink">
            {children}
          </Link>
        );
      }
      if (entryLink.__typename === 'ExternalLink' && entryLink.url) {
        return (
          <Link href={entryLink.url} className="inline underline" data-testid="inline-hyperlink">
            {children}
          </Link>
        );
      }
    }

    return <>{children}</>;
  };

  const renderHeadline = (node: Node, children: React.ReactNode) => {
    const Tag = level ?? 'h2';

    // Prevents trailing new lines in the Contentful field from rendering an empty heading
    if (children?.toString().trim() === '') return null;

    return <Tag className={cn('group', className, level)}>{children}</Tag>;
  };

  const renderSuperScript = (children: React.ReactNode) => {
    return renderSuperscript(children);
  };

  return documentToReactComponents(cleanHeadline, {
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node, children) => renderHeadline(node, children),
      [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => (
        <Link href={node.data.uri} className="inline underline" data-testid="inline-hyperlink">
          {children}
        </Link>
      ),
      [INLINES.ENTRY_HYPERLINK]: (node, children) => renderEntryLink(node, children),
      [INLINES.EMBEDDED_ENTRY]: (node: Node, children: React.ReactNode) => {
        return renderEmbeddedEntry(node, children, headline, disclosureNumberingMap);
      },
    },
    renderMark: {
      [MARKS.SUPERSCRIPT]: (children: React.ReactNode) => renderSuperScript(children),
    },
    ...additionalOptions,
  });
};

export default RichTextHeadline;
